import {
  Button,
  Container,
  Form,
  Input,
  Link,
  List,
  Message,
  Stack,
  Text,
  useBasisForm,
} from "basis";
import { CreditCard } from "components/CreditCard/CreditCard";
import { Layout } from "components/Layout/Layout";
import { Account } from "library/accounts/types";
import { Product } from "library/hooks/types";
import { useAccountInfo } from "library/hooks/useAccountInfo";
import { formatAmount } from "library/moneyUtils";
import { useNavigate } from "react-router-dom";
import { NzCreditLimitDetails } from "../components/NzCreditLimitDetails";
import { nzProductConfigs } from "../constants/constants";
import { useCliContext } from "../hooks/useCliContext";
import { ActionTypes, CliFormValues } from "../types";
import { nzValidateNewCreditLimit } from "../validation/nzCustomValidation";

type Props = {
  location: {
    state?: {
      account: Account;
      product: Product;
    };
  };
};

export const CliEligiblePage = ({ location }: Props) => {
  const { state, dispatch } = useCliContext();
  const account = location.state?.account;
  const product = location.state?.product;
  const accountInfo = useAccountInfo(account);
  const {
    cardMinMonthlyPaymentPercentage,
    cardMinMonthlyPaymentThreshold,
    cardMaxCreditLimit,
  } = nzProductConfigs[product?.code ?? ""];
  const navigate = useNavigate();
  const onSubmit = (values: CliFormValues) => {
    dispatch({
      type: ActionTypes.SUBMIT_NEW_CREDIT_LIMIT,
      newCreditLimit: values.newCreditLimit,
    });
    navigate("/increase-credit-limit/about-yourself");
  };
  const { methods, Field } = useBasisForm<CliFormValues>({
    defaultValues: {
      newCreditLimit: state.account?.product_id ? state.newCreditLimit : "",
    },
  });
  const { watch } = methods;
  const newCreditLimit = watch("newCreditLimit");
  const validateData = {
    data: {
      creditLimit: accountInfo?.creditLimit,
      maxCreditLimit: cardMaxCreditLimit,
    },
  };
  return (
    <Layout>
      <Layout.Header>Request a credit limit increase</Layout.Header>
      <Layout.Main>
        <Form methods={methods} onSubmit={onSubmit}>
          <Stack gap="8">
            <Container bg="grey.t05" padding="4">
              <CreditCard
                cardName={accountInfo?.cardName ?? ""}
                cardEndsWith={accountInfo?.cardEndsWith}
              />
            </Container>
            <Container bg="secondary.lightBlue.t25" padding="4">
              <Container bg="white" padding="6">
                <Text>
                  <strong>
                    Here’s some important information you’ll need to know about
                    increasing your credit limit.
                  </strong>
                </Text>
                <List margin="6 0 0 0">
                  <List.Item>We'll be doing a credit check</List.Item>
                  <List.Item>
                    After submitting this application, regardless of the outcome
                    of the application or if you choose to cancel it, you will
                    not be able to request for another credit limit increase
                    within 6 months.
                  </List.Item>
                  <List.Item>
                    You will need to provide proof of your income, for example,
                    this could include bank statement or payslips.
                  </List.Item>
                  <List.Item>
                    If approved your credit limit increase will be available
                    instantly.
                  </List.Item>
                </List>
                <Container padding="5 5 0 5">
                  <Link
                    href="/increase-credit-limit/important-information"
                    newTab={false}
                  >
                    Read more important information about increasing your credit
                    limit.
                  </Link>
                </Container>
              </Container>
            </Container>
            <Text as="h3" textStyle="heading4">
              What limit did you have in mind?
            </Text>
            <Text>
              Based on the current information, you're eligible to increase your
              credit limit to a{" "}
              <strong>
                maximum of {formatAmount(accountInfo?.cardMaxCreditLimit)}
              </strong>
            </Text>
            <Message severity="info-or-minor" bg="secondary.lightBlue.t25">
              <strong>Your maximum limit</strong>
              <br />
              We may be able to offer a higher limit depending upon the
              information you provide as part of this request
            </Message>
            <Container width="280">
              <Field
                as={Input}
                name="newCreditLimit"
                label="New Credit Limit"
                maxLength="5"
                variant="numeric"
                prefix="$"
                validate={nzValidateNewCreditLimit(validateData)}
              />
            </Container>
            <NzCreditLimitDetails
              newCreditLimit={newCreditLimit}
              creditLimit={accountInfo?.creditLimit ?? 0}
              cardMinMonthlyPaymentPercentage={cardMinMonthlyPaymentPercentage}
              cardMinMonthlyPaymentThreshold={cardMinMonthlyPaymentThreshold}
            />
            <Button type="submit" width="192">
              Start request
            </Button>
          </Stack>
        </Form>
      </Layout.Main>
    </Layout>
  );
};
